import { Location2D, Sparkline } from '../../charts';
import { Meter, Panel, PlayerChip } from '../ui';
import SkeletonView from '../skeleton/SkeletonView';

/**
 * Live sensor cards fed by session:suki { s: {name: bool}, r: {name: num}, l2: {name: {x,y}}, l3: {name: {x,y,z}} }
 * and by the skeleton stream (pelvis trail). Each card states plainly when nothing has arrived.
 */
const fmt = (n, dp = 2) => (Number.isFinite(Number(n)) ? Number(n).toFixed(dp) : '—');
const Waiting = ({ children }) => <p className="eg-small eg-secondary" style={{ padding: '16px 20px', margin: 0 }}>{children}</p>;

/** Signals — Suki on/off signals, a step trace of the last 30 s each. */
export function SignalsCard({ suki, history, who }) {
    const names = Object.keys(suki?.s || {});
    return (
        <Panel title="Signals" note={names.length ? 'Last 30 s' : undefined} right={who}>
            {names.length ? (
                <div className="ls-rows">
                    {names.map((n, i) => {
                        const on = !!suki.s[n];
                        return (
                            <div key={n} className="ls-sig">
                                <span className="ls-sig__name" title={n}>{n}</span>
                                <Sparkline data={history[n] || []} idx={i} min={0} max={1} step />
                                <span className="ls-sig__value" style={{ color: on ? 'var(--egdl-success-ink)' : 'var(--egdl-text-secondary)' }}>{on ? 'On' : 'Off'}</span>
                            </div>
                        );
                    })}
                </div>
            ) : <Waiting>{suki ? 'This game sends no Suki signals.' : 'Waiting for Suki data…'}</Waiting>}
        </Panel>
    );
}

/** Ranges — Suki range values with a meter. Values are what the schema outputs (usually 0–1). */
export function RangesCard({ suki, history, who }) {
    const entries = Object.entries(suki?.r || {});
    return (
        <Panel title="Ranges" right={who}>
            {entries.length ? (
                <div className="ls-rows" style={{ paddingTop: 4, paddingBottom: 8 }}>
                    {entries.map(([n, raw], i) => {
                        const v = parseFloat(raw);
                        const hist = (history[n] || []).filter(Number.isFinite);
                        const peak = hist.length ? Math.max(...hist) : null;
                        return (
                            <div key={n} className="ls-range">
                                <div className="ls-range__top">
                                    <span className="ls-range__name" title={n}>{n}</span>
                                    {peak != null ? <span className="eg-small eg-secondary eg-num">peak {fmt(peak)}</span> : null}
                                    <span className="ls-range__value">{fmt(v)}</span>
                                </div>
                                <Meter label={n} value={Number.isFinite(v) ? v : 0} mark={peak != null && peak <= 1 ? peak : null} valueText={fmt(v)} />
                            </div>
                        );
                    })}
                </div>
            ) : <Waiting>{suki ? 'This game sends no Suki ranges.' : 'Waiting for Suki data…'}</Waiting>}
        </Panel>
    );
}

/** Suki input — 2D input locations as a pad with X/Y bars; 3D locations as numbers. */
export function SukiInputCard({ suki, who }) {
    const l2 = Object.entries(suki?.l2 || {});
    const l3 = Object.entries(suki?.l3 || {});
    // Locations are normalised by the Suki schema; draw -1..1 unless every value is ≥ 0 (then 0..1).
    const signed = l2.some(([, p]) => Number(p?.x) < 0 || Number(p?.y) < 0);
    const toPct = (n) => {
        const x = Number(n);
        if (!Number.isFinite(x)) return 50;
        return Math.max(0, Math.min(100, signed ? (x + 1) * 50 : x * 100));
    };
    const first = l2[0];
    return (
        <Panel title="Suki input" right={who}>
            {first || l3.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 20 }}>
                    {first ? (
                        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div className="ls-pad" aria-label={`${first[0]} position`} role="img">
                                <div className="ls-pad__v" /><div className="ls-pad__h" />
                                <div className="ls-pad__dot" style={{ left: toPct(first[1]?.x) + '%', top: 100 - toPct(first[1]?.y) + '%' }} />
                            </div>
                            <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <span className="eg-caps-14 eg-secondary">{first[0]}</span>
                                {['x', 'y'].map((ax) => (
                                    <div key={ax} className="ls-axis">
                                        <div className="ls-axis__top"><span className="ls-axis__name">{ax.toUpperCase()}</span><span className="ls-sig__value" style={{ width: 'auto' }}>{fmt(first[1]?.[ax])}</span></div>
                                        <Meter label={`${first[0]} ${ax}`} value={toPct(first[1]?.[ax]) / 100} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {[...l2.slice(1), ...l3].length ? (
                        <div>
                            {[...l2.slice(1), ...l3].map(([n, p]) => (
                                <div key={n} className="eg-kv" style={{ minHeight: 44 }}>
                                    <span className="eg-kv__k">{n}</span>
                                    <span className="eg-kv__v">{fmt(p?.x)} · {fmt(p?.y)}{p && 'z' in p ? ' · ' + fmt(p.z) : ''}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : <Waiting>{suki ? 'This game sends no Suki input locations.' : 'Waiting for Suki data…'}</Waiting>}
        </Panel>
    );
}

/** Location 2D — top-down pelvis trail (x, z) around where the player started. */
export function Location2DCard({ trail, origin, slot = 1 }) {
    const pts = trail.map((p) => [p[0] - (origin?.[0] || 0), p[2] - (origin?.[1] || 0)]);
    const last = pts[pts.length - 1];
    return (
        <Panel title="Location 2D" note={last ? `P${slot} · x ${last[0].toFixed(2)} · z ${last[1].toFixed(2)} m` : undefined}>
            {pts.length ? <div style={{ padding: 16 }}><Location2D trail={pts} height={260} label={`Top-down pelvis position of P${slot}`} /></div> : <Waiting>Waiting for skeleton data…</Waiting>}
        </Panel>
    );
}

/** Location 3D — pelvis trail in the room. */
export function Location3DCard({ trail, origin }) {
    return (
        <Panel title="Location 3D" note="Pelvis · 15 s trail">
            <SkeletonView mode="location" location={{ points: trail }} origin={origin} height={292} emptyText="Waiting for skeleton data…" />
        </Panel>
    );
}

export function WhoChip({ slot = 1, name }) {
    return <PlayerChip variant="compact" slot={slot} name={name} showGuestNote={false} />;
}
